import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { toast } from "sonner";
import { Sidebar } from "@/components/layout/Sidebar";
import { Header } from "@/components/layout/Header";
import { Paywall } from "@/components/Paywall";
import { useAuth } from "@/context/AuthContext";

const PAYWALL_ALLOW = ["/settings"];

function ImpersonationBanner() {
  const { user, stopImpersonate } = useAuth();
  const navigate = useNavigate();
  if (!user?.is_impersonating) return null;
  const roleLabels = { super_admin: "Super Admin", tenant_admin: "Tenant Admin", manager: "Manager", buyer: "Buyer" };
  const stop = async () => {
    const back = user.impersonator_name || "your account";
    await stopImpersonate();
    const origin = localStorage.getItem("impersonationOrigin");
    localStorage.removeItem("impersonationOrigin");
    toast.success(`Returned to ${back}`);
    navigate(origin ? `/organizations/${origin}` : "/");
  };
  return (
    <div className="flex items-center justify-center gap-3 bg-[#EAB308] px-4 py-2 text-sm font-medium text-[#1A1A1A]" data-testid="impersonation-banner">
      <span className="font-semibold">Viewing as {user.name}</span>
      <span className="opacity-80">{roleLabels[user.role] || user.role}{user.tenant_name ? ` · ${user.tenant_name}` : ""}</span>
      <button onClick={stop} data-testid="stop-impersonate-btn" className="ml-2 inline-flex items-center gap-1.5 rounded-md bg-[#1A1A1A] px-3 py-1.5 text-xs font-semibold text-[#FAFAFA] transition-colors hover:bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1A1A1A]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#EAB308]">
        <LogOut className="h-3.5 w-3.5" /> Exit Impersonation
      </button>
    </div>
  );
}

export function AppLayout() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => { setMobileOpen(false); }, [pathname]);
  const sub = user && user.subscription;
  const paywalled =
    user && user.role !== "super_admin" && sub && sub.status === "expired" &&
    sub.expiry_action === "paywall" && !PAYWALL_ALLOW.some((p) => pathname.startsWith(p));

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Sidebar mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
      <div className="lg:pl-64">
        <ImpersonationBanner />
        <Header onMenuClick={() => setMobileOpen(true)} />
        <main className="mx-auto max-w-[1600px] px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
          {paywalled ? <Paywall /> : <Outlet />}
        </main>
      </div>
    </div>
  );
}
